import React, { useEffect, useState } from 'react';
import { Box, Fade, FormControlLabel, Typography } from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { DRCProfile, FieldProps } from '../../../types';
import { AppCheckbox } from '../../../components/inputs';
import { AppButton } from '../../../components/AppButton';
import { LimitedCompanyNameField } from '../../../components/controlled-inputs/LimitedCompanyNameField';
import { LimitedCompanyNumberField } from '../../../components/controlled-inputs/LimitedCompanyNumberField';
import { UTRNumberField } from '../../../components/controlled-inputs/UTRNumberField';
import { getUKFormattedDate } from '../../../utils/dates';

interface Props extends FieldProps {
  /**
   * Legend only — the Companies House lookup is an admin/v1 route. Resolves to the stored
   * snapshot, or null when the lookup failed and has already been reported.
   */
  onVerifyCompany?: (companyNumber: string) => Promise<DRCProfile | null>;
  verifying?: boolean;
  /** The last Companies House snapshot, stored on the worker's DRC profile. */
  companyCheck?: DRCProfile | null;
}

const formatRegisteredOffice = (profile: DRCProfile) => {
  const office = profile.company_registered_office;
  if (!office) return '';
  return [
    office.address_line_1,
    office.address_line_2,
    office.locality,
    office.region,
    office.postal_code,
    office.country,
  ]
    .filter(Boolean)
    .join(', ');
};

export const LimitedCompanyInputs: React.FC<Props> = ({
  disabled = false,
  onVerifyCompany,
  verifying = false,
  companyCheck,
}) => {
  const { control, getValues, setValue, watch } = useFormContext();
  const [isLimitedCompany, setIsLimitedCompany] = useState<boolean>(getValues('isLimitedCompany'));
  const companyNumber = ((watch('company_reg_number') as string | null) || '').trim();

  useEffect(() => {
    if (!isLimitedCompany) {
      setValue('company_name', null);
      setValue('company_reg_number', null);
      setValue('company_utr', null);
    }
  }, [isLimitedCompany]);

  // The name comes from Companies House rather than being typed, but it is only filled in —
  // the Legend still saves the form, so nothing changes on the employee behind their back.
  const handleVerify = async () => {
    if (!onVerifyCompany || !companyNumber) return;
    const profile = await onVerifyCompany(companyNumber);
    if (!profile) return;
    const options = { shouldDirty: true, shouldValidate: true };
    if (profile.company_name) setValue('company_name', profile.company_name, options);
    // Companies House answers with its canonical form (zero-padded, upper case).
    if (profile.company_number && profile.company_number !== companyNumber) {
      setValue('company_reg_number', profile.company_number, options);
    }
  };

  // A snapshot vouches for one number, so it is hidden as soon as the field holds another.
  const snapshot =
    companyCheck?.company_verified_at &&
    companyCheck.company_number?.toUpperCase() === companyNumber.toUpperCase()
      ? companyCheck
      : null;
  const address = snapshot ? formatRegisteredOffice(snapshot) : '';

  return (
    <>
      <Box display="flex" width="100%" marginTop={1}>
        <Controller
          name="isLimitedCompany"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <AppCheckbox
                  onChange={e => {
                    const checked = e.target.checked;
                    field.onChange(checked);
                    setIsLimitedCompany(checked);
                  }}
                  disabled={disabled}
                  color="primary"
                  checked={field.value}
                />
              }
              label="Are you in a Limited Company?"
            />
          )}
        />
      </Box>

      {isLimitedCompany && (
        <Fade timeout={500} in={true}>
          <Box style={{ width: '100%' }}>
            <LimitedCompanyNameField />

            <Box display="flex" alignItems="flex-start" gridGap={8}>
              <Box flex={1} minWidth={0}>
                <LimitedCompanyNumberField />
              </Box>
              {onVerifyCompany && (
                <AppButton
                  variant="outlined"
                  color="primary"
                  loading={verifying}
                  disabled={!companyNumber}
                  style={{ height: 56, marginTop: 16 }}
                  onClick={handleVerify}>
                  Verify
                </AppButton>
              )}
            </Box>

            {snapshot && (
              <Box marginBottom={1}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {[
                    snapshot.company_status,
                    snapshot.company_incorporated_on
                      ? `Incorporated ${getUKFormattedDate(snapshot.company_incorporated_on)}`
                      : null,
                    `Checked with Companies House ${getUKFormattedDate(snapshot.company_verified_at)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                {!!address && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {address}
                  </Typography>
                )}
              </Box>
            )}

            <UTRNumberField
              showHint={false}
              margin="normal"
              label="Limited Company UTR"
              name="company_utr"
            />
          </Box>
        </Fade>
      )}
    </>
  );
};
